import { Store } from '@ngrx/store';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  UntypedFormControl,
} from '@angular/forms';
import { CompanyInfoStoreInterface, LANGUAGE } from '@fitness-force/models';
import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { SelectModule } from 'primeng/select';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import { Observable, of } from 'rxjs';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
// import { FactoryHttpLoader } from '@fitness-force/shared';
import { HttpClient } from '@angular/common/http';
import { COMPANY_INFO } from 'apps/ff-frontend/src/app/state/app.state';

@Component({
  selector: 'app-lang-dropdown',
  templateUrl: './langdropdown.component.html',
  styleUrls: ['./langdropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    TranslateModule
  ],
})
export class LangdropdownComponent implements OnInit {
  @Input('Lang') Lang: CompanyInfoStoreInterface | any;
  @Output('EmitLang') EmitLang = new EventEmitter<any>();
  langFromGroup: UntypedFormGroup;
  companyDetails: any = { compDetails: { supported_languages: [], primary_language: 'en' } };
  public companyInfoDetail$: Observable<any> = of({});

  constructor(
    public translate: TranslateService, 
    private store: Store,  
    private cd: ChangeDetectorRef
  ) {
    // Initialize form in constructor to avoid undefined errors
    this.langFromGroup = new UntypedFormGroup({
      selectedLang: new UntypedFormControl(''),
    });
  }

  ngOnInit(): void {
    this.onComponentInit();
  }

  onComponentInit() {
    this.companyInfoDetail$ = this.store.select(COMPANY_INFO);
    
    // Set default language if Lang input is available
    if (this.Lang && this.Lang.selectedLang) {
      this.langFromGroup.patchValue({ selectedLang: this.Lang.selectedLang });
    }
    
    this.companyInfoDetail$.subscribe((responseData) => {
      if (responseData) {
        this.companyDetails = responseData || this.companyDetails;
        
        // Only set language if valid data is present
        if (responseData?.selectedLang?.LANG_CODE) {
          this.translate.setDefaultLang(responseData.selectedLang.LANG_CODE);
          this.translate.use(responseData.selectedLang.LANG_CODE);
          this.langFromGroup.get('selectedLang')?.setValue(responseData.selectedLang);
        } else {
          // Use default English if no language is specified
          this.translate.setDefaultLang('en');
          this.translate.use('en');
        }
        
        this.cd.detectChanges();
      }
    });
  }

  getLangObject(code: string) {
    return CONSTANTS_DATA.ALL_LANGUAGES.find((lang) => (lang.LANG_CODE === code));
  }

  get SelectedLang() {
    return this.langFromGroup.get('selectedLang')?.value;
  }

  get parseSupportedLang(): Array<LANGUAGE> {
    let supportedLangBuffer: Array<LANGUAGE> = [];
    
    // Handle possible undefined values safely
    if (!this.companyDetails || !this.companyDetails.compDetails) {
      return [{ COUNTRY: 'us', LANG_CODE: 'en', LANG_ABBRE: 'ENGLISH' }];
    }
    
    const supportedLang = this.companyDetails.compDetails.supported_languages ?
      this.companyDetails.compDetails.supported_languages.map(
        (l: any) => (l && typeof l === 'string') ? l.trim().toLowerCase() : ''
      ) : [];

    if (supportedLang && supportedLang.length > 0) {
      supportedLangBuffer = CONSTANTS_DATA.ALL_LANGUAGES.filter((lang) => {
        const languageOnlower = lang.LANG_ABBRE.toLowerCase();
        const langCodeOnlower = lang.LANG_CODE.toLowerCase();
        
        return (
          supportedLang.includes(languageOnlower) ||
          supportedLang.includes(langCodeOnlower)
        );
      });
    } else if (this.companyDetails.compDetails.primary_language) {
      // Find the language that matches the primary language
      const primaryLang = CONSTANTS_DATA.ALL_LANGUAGES.find(
        lang => lang.LANG_ABBRE.toLowerCase() === this.companyDetails.compDetails.primary_language.toLowerCase()
      );
      if (primaryLang) {
        supportedLangBuffer = [primaryLang];
      }
    }
    
    // Ensure we always have at least English as a fallback
    if (supportedLangBuffer.length === 0) {
      supportedLangBuffer = [{ COUNTRY: 'us', LANG_CODE: 'en', LANG_ABBRE: 'ENGLISH' }];
    }
    
    return supportedLangBuffer;
  }

  onSelectLang(e: LANGUAGE) {
    this.EmitLang.emit(e);
  }
}